import { ClickAwayListener, TextFieldProps, paperClasses } from "@mui/material";
import {
	dateCalendarClasses,
	DateTimePicker,
	DateTimePickerProps,
	LocalizationProvider,
	multiSectionDigitalClockSectionClasses,
	PickersActionBarAction,
	PickersActionBarProps,
	PickersLayoutProps,
	PickersLayoutRoot,
	pickersMonthClasses,
	pickersYearClasses,
	usePickerLayout,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import CoreTextField, { CoreTextFieldProps } from "../form/TextField";
import { CommonDatePickerProps } from "./DatePicker";
import { DateOrTimeViewWithMeridiem, pickersPopperClasses } from "@mui/x-date-pickers/internals";
import { buttonStyles, patchMonthViewArrowKeys } from "./shared";
import styles from "./index.module.css";
import { CoreButton } from "../button/Button";
import i18nPackage from "../../i18n/i18n";
import { DateOpenPickerIcon, CustomDatePickersDay, DatePickerButton } from "./DateComponents";

function CustomActionBar({ onAccept, onCancel, onClear, actions }: PickersActionBarProps) {
	if (actions?.length === 0) return null;

	return (
		<div className={styles.customActionBar}>
			{actions?.includes("clear") && (
				<CoreButton color="cancel" onClick={onClear}>
					{i18nPackage.t("coreComponents.datePicker.clear")}
				</CoreButton>
			)}
			<div className={styles.rightButtons}>
				{actions?.includes("cancel") && (
					<CoreButton color="secondary" onClick={onCancel}>
						{i18nPackage.t("coreComponents.datePicker.cancel")}
					</CoreButton>
				)}
				{actions?.includes("accept") && (
					<CoreButton onClick={onAccept}>{i18nPackage.t("coreComponents.datePicker.confirm")}</CoreButton>
				)}
			</div>
		</div>
	);
}

function CustomLayout(props: PickersLayoutProps<Date | null, Date, DateOrTimeViewWithMeridiem>) {
	const { actionBar, content } = usePickerLayout(props);
	return (
		<PickersLayoutRoot ownerState={props} className={styles.pickerRoot}>
			{content}
			{actionBar}
		</PickersLayoutRoot>
	);
}

export type CoreDateTimePickerProps = {
	value: Date | null;
	onChange: (date: Date | null) => void;
	actions?: PickersActionBarAction[];
} & CommonDatePickerProps &
	Omit<DateTimePickerProps<Date>, "value" | "onChange" | "open" | "dayOfWeekFormatter" | "slotProps"> & {
		slotProps?: Omit<Exclude<DateTimePickerProps<Date>["slotProps"], undefined>, "textField"> & {
			textField?: Partial<CoreTextFieldProps>;
		};
	};

export default function CoreDateTimePicker({
	onChange,
	value,
	error,
	ariaLabel,
	ariaLabelledBy,
	dialogAriaLabel,
	dialogAriaLabelledBy,
	fixedWeekNumber = 6,
	monthsPerRow = 4,
	width,
	actions = ["cancel", "accept"],
	showDaysOutsideCurrentMonth = true,
	dayOfWeekFormatter = (date: Date) =>
		date.toString().charAt(0).toUpperCase() + date.toString().charAt(1).toLowerCase(),
	useButton,
	...props
}: CoreDateTimePickerProps) {
	const [open, setOpen] = useState(false);
	const dialogId = useId();
	const popperRef = useRef<HTMLDivElement | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const hasInitialScrolled = useRef<boolean>(false);
	const [view, setView] = useState<DateOrTimeViewWithMeridiem>("day");

	const [draftValue, setDraftValue] = useState<Date | null>(value);

	const hasConfirm = useMemo(() => actions.includes("accept"), [actions]);

	const handleMonthViewArrowKeys = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (view !== "month" || monthsPerRow !== 4 || !popperRef.current) return;
			const container = popperRef.current.querySelector(".MuiDayCalendar-monthContainer");
			patchMonthViewArrowKeys(event, container);
		},
		[monthsPerRow, view],
	);

	const handleOpen = useCallback(() => {
		setDraftValue(value);
		setOpen(true);
		props.onOpen?.();
	}, [props, value]);

	const handleClose = useCallback(() => {
		if (open) {
			setOpen(false);
			inputRef.current?.focus();
			props.onClose?.();
		}
	}, [open, props]);

	const handleDateChange = useCallback(
		(newDate: Date | null) => {
			if (hasConfirm) {
				setDraftValue(newDate);
			} else {
				onChange(newDate);
			}
		},
		[hasConfirm, onChange],
	);

	const handleConfirm = useCallback(() => {
		onChange(draftValue);
		handleClose();
	}, [draftValue, handleClose, onChange]);

	// Patching the enter key since autoFocus is false
	const patchTimeArrows = useCallback((e: KeyboardEvent) => {
		const target = e.target as HTMLElement;

		// Check if we are on a time item
		const isItem = target.role === "option" || target.classList.contains("MuiMenuItem-root");
		if (!isItem) return;

		if (e.key === "Enter") {
			e.stopImmediatePropagation();
			e.preventDefault();
			e.stopPropagation();

			target.click();

			// Find the next column and focus the first/selected item there
			setTimeout(() => {
				const currentColumn = target.closest(".MuiMultiSectionDigitalClockSection-root");
				const nextColumn = currentColumn?.nextElementSibling;

				if (nextColumn) {
					// Find either the already selected item in the next column, or just the first one
					const itemToFocus =
						(nextColumn.querySelector('[aria-selected="true"]') as HTMLElement) ||
						(nextColumn.querySelector('[role="option"]') as HTMLElement);

					itemToFocus?.focus();
				}
			}, 50); // Small delay to let MUI finish its render/flicker cycle
		}
	}, []);

	const setRef = useCallback(
		(node: HTMLDivElement | null) => {
			popperRef.current = node;
			if (node) {
				popperRef.current?.addEventListener("keydown", patchTimeArrows, true);
				if (!hasInitialScrolled.current) {
					// Wait for mui to finish scrolling
					setTimeout(() => {
						const containers = node.querySelectorAll(
							'.MuiDigitalClock-list, [role="listbox"], .MuiMultiSectionDigitalClockSection-content',
						);
						hasInitialScrolled.current = true;

						containers.forEach((el) => {
							el.scrollTop = 0;
						});
					}, 50);
				}
			} else {
				popperRef.current?.removeEventListener("keydown", patchTimeArrows, true);
				popperRef.current = null;
			}
		},
		[patchTimeArrows],
	);

	return (
		// For some reason in chrome extension, onClose doesn't fire so using ClickAwayListener instead
		<ClickAwayListener onClickAway={handleClose}>
			<div>
				<LocalizationProvider
					dateAdapter={AdapterDateFns}
					localeText={{
						fieldYearPlaceholder: () => "YYYY",
						fieldMonthPlaceholder: () => "MM",
						fieldDayPlaceholder: () => "DD",
						fieldHoursPlaceholder: () => "hh",
						fieldMinutesPlaceholder: () => "mm",
						fieldMeridiemPlaceholder: () => "",
					}}
				>
					<DateTimePicker
						{...props}
						fixedWeekNumber={fixedWeekNumber}
						dayOfWeekFormatter={
							dayOfWeekFormatter ??
							((day: Date) => `${day.toString().charAt(0).toUpperCase()}${day.toString().charAt(1).toLowerCase()}`)
						}
						monthsPerRow={monthsPerRow}
						showDaysOutsideCurrentMonth={showDaysOutsideCurrentMonth}
						value={hasConfirm ? draftValue : value}
						onChange={handleDateChange}
						open={open}
						// When true, cannot use arrow keys to navigate the time pickers after clicking "enter" as MUI keeps focusing the selected button
						autoFocus={false}
						onOpen={handleOpen}
						onViewChange={(view) => setView(view)}
						views={props.views ?? ["year", "month", "day", "hours", "minutes"]}
						timeSteps={props.timeSteps ?? { minutes: 1 }}
						slotProps={{
							actionBar: {
								...props.slotProps?.actionBar,
								actions,
								onAccept: handleConfirm,
								onCancel: handleClose,
								onClear: () => handleDateChange(null),
							},
							textField: {
								...(props.slotProps?.textField as TextFieldProps),
								error: error as never,
								...(hasConfirm && { value: value }),
								sx: {
									fontVariantNumeric: "tabular-nums",
									margin: 0,
									width: width ?? "100%",
									...(props.slotProps?.textField && "sx" in props.slotProps.textField
										? props.slotProps.textField.sx
										: undefined),
								},
								inputProps: {
									...props.slotProps?.textField?.inputProps,
									role: "combobox",
									"aria-haspopup": "dialog",
									"aria-expanded": open,
									"aria-controls": dialogId,
									"aria-label": ariaLabel,
									"aria-labelledby": ariaLabelledBy,
									inputRef,
								},
								onClick: (e) => {
									if (useButton) {
										handleOpen();
									}
									props.slotProps?.textField?.onClick?.(e);
								},
							},
							popper: {
								// Temporoary half solution for firefox because the poppers are not flipping (?) and so the poppers don't show unless there is room at the bottom
								placement: "bottom-start",
								id: dialogId,
								"aria-modal": true,
								"aria-label": dialogAriaLabel ?? ariaLabel,
								"aria-labelledby": dialogAriaLabelledBy ?? ariaLabelledBy,
								ref: setRef,
								onKeyDown: (e) => {
									handleMonthViewArrowKeys(e);
									if (e.key === "Escape") {
										e.stopPropagation();
										handleClose();
									}
								},
								...props.slotProps?.popper,
								sx: {
									// popper border/shadow
									[`& .${pickersPopperClasses.paper}.${paperClasses.root} `]: {
										borderRadius: "12px",
										border: "none",
										boxShadow: "0px 2px 8px 0px #2320202a",
									},
									// remove button ripples
									[" .MuiTouchRipple-root"]: {
										display: "none",
									},
									[`& .${dateCalendarClasses.root}`]: {
										[`& .MuiPickersCalendarHeader-root`]: {
											padding: "0 0.75rem",
											position: "relative",
											[`& .MuiPickersCalendarHeader-labelContainer`]: {
												[`& .MuiPickersCalendarHeader-label`]: {
													fontWeight: "700",
													textAlign: "center",
													display: "flex",
													justifyContent: "center",
													borderRadius: "20px",
													margin: "0",
													padding: "0.5rem",
												},
												[`&:hover`]: {
													[`& .MuiPickersCalendarHeader-label`]: {
														backgroundColor: "#eceaf1",
													},
												},
												[`&:focus, &:focus-within`]: {
													[`& .MuiPickersCalendarHeader-label`]: {
														backgroundColor: "inherit",
														outline: "2px solid var(--focus-outline-color-dark)",
														outlineOffset: "-2px",
													},
													[`&:hover .MuiPickersCalendarHeader-label`]: {
														backgroundColor: "#e3dfe9",
													},
													[`&:active .MuiPickersCalendarHeader-label`]: {
														backgroundColor: "#c4bdd2",
													},
												},
												[`&:active`]: {
													[`& .MuiPickersCalendarHeader-label`]: {
														backgroundColor: "#e3dfe9",
													},
												},
												[`& .MuiPickersCalendarHeader-switchViewButton`]: {
													opacity: "0",
													width: "0",
													position: "0",
													margin: "0",
													padding: "0",
												},
											},
											[`& .MuiPickersArrowSwitcher-button`]: {
												height: "37px",
												width: "37px",
												[`&:hover`]: {
													backgroundColor: "#eceaf1",
												},
												[`&:focus`]: {
													outline: "2px solid var(--focus-outline-color-dark)",
													backgroundColor: "inherit",
												},
												[`&:active`]: {
													backgroundColor: "#e3dfe9",
												},
												[`&:disabled:focus`]: {
													backgroundColor: "#e3dfe9",
												},
											},
										},
										[`& .MuiDayCalendar-weekDayLabel`]: {
											fontSize: "13px",
											height: "26px",
											width: "26px",
											margin: "9px 5px 3px",
										},
										// month and year calendar
										[`& .MuiMonthCalendar-root, & .MuiYearCalendar-root`]: {
											height: "244px",
											width: "90%",
											padding: "0",
											margin: "auto",
										},
										[`& .${pickersMonthClasses.root}`]: {
											height: "25%",
										},
										[`& .${pickersMonthClasses.monthButton}`]: {
											fontSize: "12px",
											height: "36px",
											width: "36px",
											margin: "5px",
											...buttonStyles,
										},
										[`& .${pickersYearClasses.yearButton}`]: {
											fontSize: "12px",
											height: "42px",
											width: "42px",
											borderRadius: "21px",
											margin: "5px",
											marginY: "19px",
											...buttonStyles,
										},
									},
									[`& .MuiMultiSectionDigitalClockSection-item`]: {
										...buttonStyles,
										borderRadius: "6px",
										// MUI gives this a fixed width. With a fine pointer, hovering the
										// hours/minutes column switches it from overflow: hidden to auto, and
										// the scrollbar that appears eats into the content box, clipping the
										// fixed-width (and Mui-selected) item's right edge. Size relative to
										// the column instead so it always fits, scrollbar or not.
										width: "calc(100% - 8px)",
									},
									[`& .${multiSectionDigitalClockSectionClasses.root}:after`]: {
										display: "none",
									},
									...(props.slotProps?.popper && "sx" in props.slotProps.popper
										? props.slotProps.popper.sx
										: undefined),
								},
							},
							openPickerButton: {
								...props.slotProps?.openPickerButton,
								disableFocusRipple: true,
								sx: {
									"&:focus-visible": {
										outline: "2px solid var(--focus-outline-color-dark)",
									},
									"&&&&&": {
										padding: "0px",
										marginRight: "-3px", // Move icon a little to the left so focus outline has room to breathe
										height: "24px",
										width: "24px",
										borderRadius: "2px",
										// Centering the icon
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									},
									...(props.slotProps?.openPickerButton && "sx" in props.slotProps.openPickerButton
										? props.slotProps.openPickerButton.sx
										: undefined),
								},
							},
							openPickerIcon: {
								fontSize: "small",
								...props.slotProps?.openPickerIcon,
							},
						}}
						slots={{
							day: CustomDatePickersDay,
							openPickerIcon: DateOpenPickerIcon,
							textField: useButton ? DatePickerButton : CoreTextField,
							actionBar: CustomActionBar,
							layout: CustomLayout,
						}}
						format={props.format ?? (value ? "MMM do yyyy, h:mm a" : "yyyy-MM-dd HH:mm")}
					/>
				</LocalizationProvider>
			</div>
		</ClickAwayListener>
	);
}
